import * as THREE from "three";

interface Bird {
  position: THREE.Vector3;
  velocity: THREE.Vector3;
  mesh: THREE.Mesh;
}

export default class BirdsBackground {
  private scene = new THREE.Scene();
  private camera: THREE.PerspectiveCamera;
  private renderer: THREE.WebGLRenderer;
  private birds: Bird[] = [];
  private frameId = 0;

  private BIRD_COUNT = 60;
  private BOUNDS = 200;

  constructor(private container: HTMLElement) {
    this.camera = new THREE.PerspectiveCamera(
      60,
      container.clientWidth / container.clientHeight,
      1,
      1000,
    );
    this.camera.position.z = 300;

    this.renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.setPixelRatio(window.devicePixelRatio);
    container.appendChild(this.renderer.domElement);

    this.initBirds();
    this.animate();
    window.addEventListener("resize", this.onResize);
  }

  private initBirds() {
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute(
      "position",
      new THREE.Float32BufferAttribute([0, 0, 0, -2, 1, 0, -2, -1, 0], 3),
    );

    const material = new THREE.MeshBasicMaterial({
      color: 0x69c0ff,
      side: THREE.DoubleSide,
    });

    for (let i = 0; i < this.BIRD_COUNT; i++) {
      const mesh = new THREE.Mesh(geometry, material);
      const bird: Bird = {
        position: new THREE.Vector3(
          (Math.random() - 0.5) * this.BOUNDS,
          (Math.random() - 0.5) * this.BOUNDS,
          (Math.random() - 0.5) * this.BOUNDS,
        ),
        velocity: new THREE.Vector3(
          (Math.random() - 0.5) * 2,
          (Math.random() - 0.5) * 2,
          (Math.random() - 0.5) * 2,
        ),
        mesh,
      };
      mesh.position.copy(bird.position);
      this.scene.add(mesh);
      this.birds.push(bird);
    }
  }

  private animate = () => {
    this.updateBoids();
    this.renderer.render(this.scene, this.camera);
    this.frameId = requestAnimationFrame(this.animate);
  };

  private updateBoids() {
    const separationDist = 15;
    const alignmentDist = 40;
    const cohesionDist = 40;

    this.birds.forEach((bird) => {
      const separation = new THREE.Vector3();
      const alignment = new THREE.Vector3();
      const cohesion = new THREE.Vector3();
      let count = 0;

      this.birds.forEach((other) => {
        const d = bird.position.distanceTo(other.position);
        if (other !== bird && d < cohesionDist) {
          alignment.add(other.velocity);
          cohesion.add(other.position);
          count++;
          if (d < separationDist)
            separation.subVectors(bird.position, other.position);
        }
      });

      if (count > 0) {
        alignment.divideScalar(count).sub(bird.velocity).multiplyScalar(0.05);
        cohesion.divideScalar(count).sub(bird.position).multiplyScalar(0.01);
      }

      separation.multiplyScalar(0.1);
      bird.velocity.add(alignment).add(cohesion).add(separation);
      bird.velocity.clampLength(0, 2);
      bird.position.add(bird.velocity);

      if (bird.position.x > this.BOUNDS) bird.position.x = -this.BOUNDS;
      if (bird.position.x < -this.BOUNDS) bird.position.x = this.BOUNDS;

      if (bird.position.y > this.BOUNDS) bird.position.y = -this.BOUNDS;
      if (bird.position.y < -this.BOUNDS) bird.position.y = this.BOUNDS;

      if (bird.position.z > this.BOUNDS) bird.position.z = -this.BOUNDS;
      if (bird.position.z < -this.BOUNDS) bird.position.z = this.BOUNDS;

      bird.mesh.position.copy(bird.position);
      bird.mesh.lookAt(bird.position.clone().add(bird.velocity));
    });
  }

  private onResize = () => {
    const { clientWidth, clientHeight } = this.container;
    this.camera.aspect = clientWidth / clientHeight;
    this.camera.updateProjectionMatrix();
    this.renderer.setSize(clientWidth, clientHeight);
  };

  public destroy() {
    cancelAnimationFrame(this.frameId);
    window.removeEventListener("resize", this.onResize);
    this.renderer.dispose();
    this.container.removeChild(this.renderer.domElement);
  }
}
